<template>
  <div>

  <!-- Client-side + server-side validation summary, shown at the top of the step. -->
  <div v-if="summary.length" class="quote-error-summary" role="alert">
    <!--<p class="qes-title"><em class="far fa-triangle-exclamation mr-1"></em> Please check the following:</p>-->
    <p v-if="summary.length === 1" class="qes-single">{{ summary[0] }}</p>
    <ul v-else>
      <li v-for="(msg, i) in summary" :key="i">{{ msg }}</li>
    </ul>
  </div>

  <Vueform
    :id="`initialData-${tabId}`"
    :key="`${tabId}-shadow-${shadowVersion}`"
    ref="formRef"
    :modelValue="formModel"
    @update:modelValue="onFormUpdate"
    :schema="schemaForForm"
    :endpoint="false"
    sync
    @submit="submitForm"
    @back="emit('nav-back')"
  />
  <LoaderOverlay
                :show="loading"
                title="Please wait"
                text="Do not refresh the page and wait while we are processing your information."
            />
  </div>
</template>


<script setup lang="ts">
import { useRiskStore } from '@/stores/riskData'
import { storeToRefs } from 'pinia'
import { ref, onMounted, watch, nextTick, provide } from 'vue'
import IMask from 'imask'
import LoaderOverlay from "@/components/Common/LoaderOverlay.vue";
import { deepMergeNoNull, deepClone } from '@/utils/objectUtils'
import { clearHiddenFields } from '@/utils/formConditions'
import { useShadowSchema } from '@/composables/useShadowSchema'
import { useQuoteErrors, serverFieldErrors } from '@/composables/useQuoteErrors'


const formRef = ref<any>(null)
const vueformApi = ref<any>(null)
const quoteErrors = useQuoteErrors()
const { summary, attemptId } = quoteErrors
const store = useRiskStore()
const { riskData, coverDetails, assumptions } =
  storeToRefs(store)

  defineOptions({
    name: 'QuestionSet'
})

const emit = defineEmits<{
  (e: 'nav-back'): void
  (e: 'nav-next'): void
  (e: 'submit-initial', value: any): void
  (e: 'update:formData', value: any): void
}>()

provide('vueformApi', vueformApi)

interface InitialQuestionsProps {
  formSchema: Record<string, any>
  riskTemplate: Record<string, any>
  mapToRisk: (mergedData: any, options: any) => any,
  additionalFormData: Record<string, any>,
  tabId: string,
  schemaKey: string,
  formData: Record<string, any>,
  // Top-level model keys owned by other steps that this step must not wipe on
  // save (see clearHiddenFields' `protectedRoots`).
  foreignRoots?: string[]
}

const props = defineProps<InitialQuestionsProps>()

// Review-mode original-value boxes (no-op when not in review).
const { schemaForForm, shadowVersion, commitShadow } = useShadowSchema(() => props.formSchema)

const formModel = ref<Record<string, any>>({})
const initialised = ref(false)
let loading = ref(false);

// Seed formModel from the parent's formData slice for this tab
watch(
  () => props.formData?.[props.schemaKey],
  (slice) => {
    if (slice && !initialised.value) {
      formModel.value = deepMergeNoNull({ ...(formModel.value ?? {}) }, slice)
    }
  },
  { immediate: true }
)

// Watch for additionalFormData and merge it into formModel
watch(
  () => props.additionalFormData,
  (newData) => {
    if (newData && Object.keys(newData).length > 0) {
      formModel.value = deepMergeNoNull({ ...(formModel.value ?? {}) }, newData)
    }
  },
  { deep: true, immediate: true }
)

watch(
  () => ({ schemaKey: props.schemaKey, risk: riskData.value, cover: coverDetails.value, assumptions: assumptions.value }),
  ({ schemaKey, risk, cover, assumptions: assumptionData }) => {
    if (initialised.value) return

    const source = schemaKey === 'coverDetails'
      ? (cover ?? risk)
      : schemaKey === 'assumptions'
        ? (assumptionData ?? risk)
        : risk

    if (!source) return

    try {
      formModel.value = buildRisk({}, source)
    } catch (e) {
      formModel.value = {}
    }

    // Re-apply additionalFormData so it isn't lost after the risk data overwrite
    if (props.additionalFormData && Object.keys(props.additionalFormData).length > 0) {
      formModel.value = deepMergeNoNull({ ...(formModel.value ?? {}) }, props.additionalFormData)
    }

    initialised.value = true
  },
  { immediate: true }
)

function buildRisk(base: any, patch: any) {
  return deepMergeNoNull(deepMergeNoNull(deepClone(props.riskTemplate), base ?? {}), patch ?? {})
}

// -=== UNSURE IF NEEDED CURRENTLY ===- 

function onFormUpdate(newValue: any) {
  // capture the vueform runtime as soon as Vueform starts emitting updates
  if (!vueformApi.value) {
    const runtime = formRef.value?.form$
    if (runtime?.el$ && runtime?.update) {
      vueformApi.value = runtime
    }
  }

  const current = formModel.value || {}
  const merged = deepMergeNoNull({ ...current }, newValue)

  formModel.value = merged

  // Propagate this tab's data back to the parent's shared formData
  emit('update:formData', {
    ...props.formData,
    [props.schemaKey]: merged
  })


  // @TODO - move store set to factory
  // const sectionKey = props.schemaKey === 'coverDetails'
  //   ? 'coverDetails'
  //   : props.schemaKey === 'assumptions'
  //     ? 'assumptions'
  //     : 'riskData'

  // const base = sectionKey === 'coverDetails'
  //   ? coverDetails.value
  //   : sectionKey === 'assumptions'
  //     ? assumptions.value
  //     : riskData.value

  // //store.setFormDataSection(sectionKey, buildRisk(base, merged))
}

async function submitForm(form$: any) {
  loading.value = true;

  // Wipe answers for fields whose conditions are no longer met (hidden
  // branches), so stale data from a path the user backed out of isn't saved.
  // Done here at save/next — not on change — using the live form's condition
  // state. Applied to the accumulated model and again to the merged result,
  // since the stored `base` may reintroduce previously-saved stale values.
  // First pass strips this form's own hidden branches — including any hidden
  // mirror of another step's namespace, so those mirrors' schema defaults (e.g.
  // Block A's `property_undergoing_works_renovation: true`) don't leak forward.
  const cleanedModel = clearHiddenFields(form$, formModel.value)
  formModel.value = cleanedModel

  const base = props.schemaKey === 'coverDetails'
    ? coverDetails.value
    : props.schemaKey === 'assumptions'
      ? assumptions.value
      : riskData.value
  // Second pass runs over the merged result, but must not wipe namespaces owned
  // by other steps: `base` carries their real saved answers, and this step's
  // hidden mirror of them would otherwise delete them (the fix for renovation
  // answers going blank on edit). The owning step still cleans its own branches.
  const mergedRiskData = clearHiddenFields(form$, buildRisk(base, cleanedModel), props.foreignRoots ?? [])

  // On Next: snapshot the shadow to the current risk data (boxes show last-saved state).
  commitShadow(mergedRiskData)

  emit('submit-initial', {
    mergedData: mergedRiskData,
    formModel: cleanedModel,
    schemaKey: props.schemaKey,
  })
}

const opts = {
  mask: Number,
  scale: 2,
  radix: '.',
  thousandsSeparator: ',',
  padFractionalZeros: true,
  normalizeZeros: true,
  min: 0.01,
  autofix: true,
}

function applyMoneyMasks(root: ParentNode = document) {
  root.querySelectorAll<HTMLInputElement>('input[data-money]').forEach((el) => {
    if ((el as any)._im) return
    ;(el as any)._im = IMask(el, opts)
  })
}

onMounted(async () => {
  // Fresh step — drop any banner/field errors left over from a previous step.
  quoteErrors.clear()
  await nextTick()
  applyMoneyMasks()
})

// When the server flags fields, re-validate so the no_errors rule surfaces them inline.
watch(() => Object.keys(serverFieldErrors).length, (n) => {
  if (n > 0) nextTick(() => vueformApi.value?.validate?.())
})

// Failed submit (attemptId increments on every client/server failure): scroll to the first invalid field.
const SCROLL_HEADROOM = 100
watch(attemptId, () => {
  loading.value = false
  nextTick(() => setTimeout(() => {
    const root = (formRef.value?.$el as HTMLElement | undefined) ?? document
    const target = (root.querySelector('.vf-element-layout-error, [aria-invalid="true"]')
      ?? document.querySelector('.quote-error-summary')) as HTMLElement | null
    if (!target) return
    const top = target.getBoundingClientRect().top - document.body.getBoundingClientRect().top - SCROLL_HEADROOM
    try { window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' }) } catch { window.scrollTo(0, Math.max(top, 0)) }
  }, 100))
})

watch(
  () => props.formSchema,
  async () => {
    await nextTick()
    applyMoneyMasks()
  },
  { deep: true }
)
</script>